import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router';

import { UnsavedChangesModalView } from './unsaved-changes-modal-view';

/**
 * Reject `onSave` with this when the save was refused because the form itself is
 * invalid, as opposed to a save that was attempted and failed.
 *
 * The two need opposite handling: a failed save is worth retrying from inside the
 * modal, but an invalid form can only be fixed on the page *behind* it — so the
 * guard closes itself and hands the user back to their fields instead of showing
 * a "try again" it knows can never succeed.
 */
export class UnsavedChangesValidationError extends Error {
  constructor() {
    super('unsaved-changes-validation-failed');
    this.name = 'UnsavedChangesValidationError';
  }
}

export interface UseUnsavedChangesOptions {
  isDirty: boolean;
  /**
   * Resolve on success (navigation then proceeds); reject on failure (inline
   * error, modal stays open). Reject with `UnsavedChangesValidationError` to
   * close the modal and return the user to the form. Must NOT navigate — the
   * blocker owns the transition.
   */
  onSave: () => Promise<void>;
  message?: string;
}

export interface UseUnsavedChangesResult {
  modal: ReactNode;
  /**
   * Suppresses the guard for the next navigation only. Call immediately before
   * a consumer's own `navigate()` that follows a successful save: the form is
   * still dirty at that instant (react-hook-form clears `isDirty` on the next
   * render), so the blocker would otherwise intercept the navigation the user
   * just asked for.
   */
  allowNavigationOnce: () => void;
}

/**
 * Guards both exits while `isDirty`: react-router navigation via `useBlocker`
 * (this modal) and leaving the app via `beforeunload` (the browser's own
 * dialog — a custom modal is impossible there).
 *
 * `open` is derived from `blocker.state` rather than toggled imperatively so a
 * programmatic proceed/reset can't race the modal's async `close` event.
 */
export const useUnsavedChanges = ({
  isDirty,
  onSave,
  message,
}: UseUnsavedChangesOptions): UseUnsavedChangesResult => {
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaveError, setHasSaveError] = useState(false);

  // Read by the botFlowError backstop, which must not re-subscribe on each save.
  const isSavingRef = useRef(isSaving);
  isSavingRef.current = isSaving;

  const bypassRef = useRef(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (bypassRef.current) {
      bypassRef.current = false;
      return false;
    }

    return isDirty && currentLocation.pathname !== nextLocation.pathname;
  });

  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  // returnValue as well as preventDefault: some Chromium/WebKit versions still
  // require the former to surface the native dialog.
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const isBlocked = blocker.state === 'blocked';

  useEffect(() => {
    if (isBlocked) setHasSaveError(false);
  }, [isBlocked]);

  const allowNavigationOnce = useCallback(() => {
    bypassRef.current = true;
  }, []);

  const handleStay = useCallback(() => {
    if (blockerRef.current.state === 'blocked') blockerRef.current.reset();
  }, []);

  const handleLeave = useCallback(() => {
    if (blockerRef.current.state === 'blocked') blockerRef.current.proceed();
  }, []);

  const handleSaveAndLeave = useCallback(async () => {
    setIsSaving(true);
    setHasSaveError(false);
    try {
      await onSave();
      if (blockerRef.current.state === 'blocked') blockerRef.current.proceed();
    } catch (error) {
      if (error instanceof UnsavedChangesValidationError) {
        // Reset, not proceed — the edits must survive. Closing returns the user
        // to the inline field errors, which are unreachable behind the modal.
        if (blockerRef.current.state === 'blocked') blockerRef.current.reset();
      } else {
        // The caller already surfaces the specific error (e.g. a global toast).
        setHasSaveError(true);
      }
    } finally {
      setIsSaving(false);
    }
  }, [onSave]);

  // Backstop for a save that routes the machine to botFlowError without ever
  // settling the onSave promise (e.g. an invoke that rejects before calling
  // errorCb) — the modal would otherwise hang on "Saving…" forever.
  const handleBotFlowError = useCallback(() => {
    if (!isSavingRef.current) return;
    setIsSaving(false);
    setHasSaveError(true);
  }, []);

  const modal = (
    <UnsavedChangesModalView
      open={isBlocked}
      isSaving={isSaving}
      hasSaveError={hasSaveError}
      message={message}
      onStay={handleStay}
      onLeave={handleLeave}
      onSaveAndLeave={handleSaveAndLeave}
      onError={handleBotFlowError}
    />
  );

  return { modal, allowNavigationOnce };
};
